import {useState} from 'react';
import {Button, CircularProgress, Stack, Typography} from '@mui/material';
import {Attachment, AttachmentDataFile} from '../../../backend/interfaces/attachment';
import './view-attachments.scss';
import {FiDownload} from 'react-icons/fi';
import {translations} from '../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {BackendApiManager} from '../../../managers/backend-api';

type ViewAttachmentsProps = {
  attachments: Attachment[];
  isDailyCareLogApp?: boolean;
};

export const ViewAttachments = ({attachments, isDailyCareLogApp}: ViewAttachmentsProps) => {
  const {t} = useTranslation();
  const [downloadingFiles, setDownloadingFiles] = useState<Record<string, boolean>>({});

  const cardStyle = {
    background: isDailyCareLogApp ? 'var(--dcm-white-base)' : 'var(--dcm-bgseniormenu)',
    boxShadow: isDailyCareLogApp ? '0px 4px 16px 0px rgba(29, 41, 57, 0.1) ' : 'none',
  };

  const handleDownload = async (fileKey: string, fileUrl: string, fileName: string) => {
    if (downloadingFiles[fileKey]) return;

    setDownloadingFiles((prev) => ({...prev, [fileKey]: true}));

    try {
      const response = await BackendApiManager.instance.get(fileUrl, {
        responseType: 'blob',
      });
      const downloadUrl = window.URL.createObjectURL(response.data as Blob);

      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.setAttribute('download', fileName);
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading the file:', error);
    } finally {
      setDownloadingFiles((prev) => {
        const next = {...prev};
        delete next[fileKey];
        return next;
      });
    }
  };

  return (
    <Stack className="attachments-card" style={cardStyle}>
      <Typography className="attachments-title">
        {t(translations.care_and_activities.handle_task_modal.files)}
      </Typography>

      <Stack gap={2} flexDirection="column">
        {attachments.map((attachment, index) => {
          const data = attachment.data as AttachmentDataFile;
          const pathname = new URL(data.fileUrl).pathname;
          const name = attachment.name ?? pathname.substring(pathname.lastIndexOf('/') + 1);
          const fileKey = attachment.id ?? data.fileUrl ?? `${index}-${name}`;
          const isDownloading = !!downloadingFiles[fileKey];

          return (
            <Stack
              key={`${index}-${name}`}
              className="file-container"
              flexDirection="row"
              alignItems="center"
              onClick={() => void handleDownload(fileKey, data.fileUrl, name)}
              sx={{
                opacity: isDownloading ? 0.7 : 1,
                cursor: isDownloading ? 'progress' : 'pointer',
              }}
            >
              <Button className="download" disabled={isDownloading}>
                {isDownloading ? (
                  <CircularProgress size={14} sx={{marginRight: '6px'}} />
                ) : (
                  <FiDownload className="icon" />
                )}
              </Button>
              <Typography className="file-name">{name}</Typography>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};
